import Image from "next/image";
import Link from "next/link";
import { Facebook, Instagram, Mail, Store } from "lucide-react";
import { subscribeToNewsletter } from "@/actions";
import { SiteHeader } from "@/components/SiteHeader";

export function LocalBuzzShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="local-buzz min-h-screen bg-background font-body text-foreground antialiased">
      <SiteHeader />
      <div className="min-h-screen text-foreground">
        {children}
      </div>
    </main>
  );
}

export function LocalBuzzFooter() {
  return (
    <footer className="local-buzz border-t border-border bg-ink-2">
      <div className="mx-auto grid max-w-[1400px] gap-12 px-6 py-16 text-sm text-muted-foreground lg:grid-cols-[1.2fr_1fr_1fr_1fr_1fr]">
        <div>
          <Link href="/" className="inline-block">
            <Image src="/logo.webp" alt="The Neighbourhood Voice" width={180} height={54} className="h-14 w-auto object-contain" />
          </Link>
          <p className="mt-5 max-w-sm text-[14px]">
            The local hub for Sydney - built by neighbours, for neighbours. Stories, services and the small businesses we love.
          </p>
          <form action={subscribeToNewsletter} className="mt-6 flex max-w-sm items-center gap-2">
            <input type="hidden" name="source" value="local-buzz-footer" />
            <div className="relative flex-1">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                name="email"
                type="email"
                required
                placeholder="Get the monthly edition"
                className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-[13px] outline-none transition focus:border-brand"
              />
            </div>
            <button className="rounded-full bg-brand px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.16em] text-white transition hover:opacity-90">
              Join
            </button>
          </form>
        </div>

        <FooterCol
          title="Services"
          items={[
            { label: "NV Tradies", href: "/services/tradies" },
            { label: "NV Property", href: "/services/property" },
            { label: "NV Eats", href: "/services/eats" },
            { label: "NV Classes", href: "/services/classes" },
            { label: "NV Wellness", href: "/services/wellness" },
            { label: "NV Charities", href: "/services/charities" }
          ]}
        />
        <FooterCol
          title="Discovery"
          items={[
            { label: "NV Deals", href: "/deals" },
            { label: "NV Events", href: "/events" },
            { label: "NV Markets", href: "/markets" },
            { label: "NV Directory", href: "/directory" },
            { label: "NV Magazine", href: "/magazine" }
          ]}
        />
        <FooterCol
          title="For Business"
          items={[
            { label: "List your business", href: "/business/listing" },
            { label: "Business portal", href: "/business" },
            { label: "Advertise", href: "/#contact" },
            { label: "Partner with us", href: "/#contact" }
          ]}
        />
        <FooterCol
          title="About"
          items={[
            { label: "Home", href: "/" },
            { label: "Contact", href: "/#contact" },
            { label: "Bookmarks", href: "/bookmarks" },
            { label: "Account", href: "/account" }
          ]}
        />
      </div>

      <div className="border-t border-border">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-3 px-6 py-5 text-xs text-muted-foreground sm:flex-row">
          <div>Copyright 2026 Neighbourhood Voice - Made in Sydney</div>
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/neighbourhood_voice_/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-brand">
              <Instagram className="h-4 w-4" />
            </a>
            <a href="https://www.facebook.com/people/Neighbourhood-Voice/61577637255454/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-brand">
              <Facebook className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }: { title: string; items: { label: string; href: string }[] }) {
  return (
    <div>
      <div className="mb-4 text-[11px] font-black uppercase tracking-[0.22em] text-foreground">{title}</div>
      <ul className="space-y-3">
        {items.map((item) => (
          <li key={item.label}>
            <Link href={item.href} className="hover:text-brand">
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LocalBuzzBusinessCta() {
  return (
    <section className="border-y border-border bg-ink-2/60">
      <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand">
            <span className="font-display italic">advertise.</span> - For local business
          </div>
          <h2 className="mt-3 max-w-3xl font-display text-4xl font-black leading-tight sm:text-5xl">
            Advertise with <span className="font-script font-normal text-brand">Neighbourhood Voice</span>
          </h2>
          <p className="mt-4 max-w-2xl text-[15px] text-muted-foreground">
            Reach thousands of locals every month - across our magazine, directory and weekly highlights.
          </p>
        </div>
        <Link href="/business/listing" className="inline-flex items-center gap-2 self-start rounded-md bg-brand px-6 py-3.5 text-[12px] font-black uppercase tracking-[0.16em] text-white transition hover:opacity-90 lg:self-auto">
          <Store className="h-4 w-4" /> List your business
        </Link>
      </div>
    </section>
  );
}
